type PositionTradeSide = 'BUY' | 'SELL';

export type SettlementClosedLegBasisType = 'MATCHED_FILL' | 'AVG_BASIS';
export type SettlementSettledLegBasisType = 'NET_OPEN';

export interface SettlementClosedLeg {
  entrySide: PositionTradeSide;
  exitSide: PositionTradeSide;
  entryPrice: number;
  exitPrice: number;
  quantity: number;
  pnl: number;
  basisType: SettlementClosedLegBasisType;
  executedAt: string | null;
  entryTradeCount: number;
  exitTradeCount: number;
  sourceTradeCount: number;
}

export interface SettlementSettledLeg {
  side: 'LONG' | 'SHORT';
  entryPrice: number | null;
  floorPrice: number | null;
  quantity: number;
  pnl: number | null;
  basisType: SettlementSettledLegBasisType;
  executedAt: string | null;
  sourceTradeCount: number;
}

export interface PositionTradeLike {
  team_code: string;
  side: PositionTradeSide;
  trade_type: string;
  trade_id: number;
  quantity: number;
  price: number;
  executed_at?: string | null;
  counterparty_trader_code: string | null;
}

export interface SettlementTradeLike extends PositionTradeLike {
  settlement_pnl?: number | null;
  settlement_floor_price?: number | null;
  settlement_avg_cost?: number | null;
  settlement_source_trade_ids?: readonly number[] | null;
  settlement_total_pnl?: number | null;
  settlement_trading_pnl?: number | null;
  settlement_open_pnl?: number | null;
  settlement_net_quantity?: number | null;
  settlement_entry_price?: number | null;
  settlement_closed_quantity?: number | null;
  settlement_closed_entry_price?: number | null;
  settlement_closed_exit_price?: number | null;
  settlement_position_side?: 'LONG' | 'SHORT' | null;
}

export interface SettlementTradeAudit<TTrade extends PositionTradeLike> {
  positionSide: 'LONG' | 'SHORT';
  settledQuantity: number;
  averageEntryPrice: number | null;
  settlementFloorPrice: number | null;
  totalPnl: number | null;
  tradingPnl: number | null;
  openSettlementPnl: number | null;
  closedQuantity: number;
  closedEntryPrice: number | null;
  closedExitPrice: number | null;
  hasClosedBeforeSettlement: boolean;
  closedLegs: SettlementClosedLeg[];
  settledLeg: SettlementSettledLeg | null;
  sourceTrades: TTrade[];
}

interface BuildPositionTradeColumnsArgs<TTrade extends PositionTradeLike> {
  positionTeamCode: string;
  trades: readonly TTrade[];
  totalTradingPnl?: number | null;
  realizedPnl?: number | null;
  unrealizedPnl?: number | null;
}

export function groupPositionTradesByTeamCode<TTrade extends PositionTradeLike>(
  trades: readonly TTrade[],
) {
  const grouped = new Map<string, TTrade[]>();

  for (const trade of trades) {
    if (trade.trade_type === 'SETTLEMENT') continue;
    const teamCode = trade.team_code.trim().toUpperCase();
    const existing = grouped.get(teamCode);
    if (existing) {
      existing.push(trade);
    } else {
      grouped.set(teamCode, [trade]);
    }
  }

  return grouped;
}

function summarizeTrades<TTrade extends PositionTradeLike>(trades: readonly TTrade[]) {
  const totalQuantity = trades.reduce((sum, trade) => sum + trade.quantity, 0);
  if (totalQuantity <= 0) {
    return {
      totalQuantity: 0,
      averagePrice: null,
    };
  }

  const notional = trades.reduce((sum, trade) => sum + trade.quantity * trade.price, 0);
  return {
    totalQuantity,
    averagePrice: notional / totalQuantity,
  };
}

function roundPnl(value: number) {
  return Math.round(value * 10000) / 10000;
}

function sortTradesAscending<TTrade extends PositionTradeLike>(trades: readonly TTrade[]) {
  return [...trades].sort((left, right) => {
    const leftTime = left.executed_at ? new Date(left.executed_at).getTime() : 0;
    const rightTime = right.executed_at ? new Date(right.executed_at).getTime() : 0;
    if (leftTime !== rightTime) return leftTime - rightTime;
    return left.trade_id - right.trade_id;
  });
}

function calculateMaterializedTradePnl<TTrade extends PositionTradeLike>(trades: readonly TTrade[]) {
  let signedQuantity = 0;
  let averageEntry = 0;
  let realizedPnl = 0;

  for (const trade of sortTradesAscending(trades)) {
    const tradeQuantity = trade.quantity;
    if (tradeQuantity <= 0) continue;

    const signedDelta = trade.side === 'BUY' ? tradeQuantity : -tradeQuantity;
    if (signedQuantity === 0 || Math.sign(signedQuantity) === Math.sign(signedDelta)) {
      const nextAbsQuantity = Math.abs(signedQuantity) + tradeQuantity;
      averageEntry =
        nextAbsQuantity > 0
          ? (Math.abs(signedQuantity) * averageEntry + tradeQuantity * trade.price) / nextAbsQuantity
          : 0;
      signedQuantity += signedDelta;
      continue;
    }

    const closeQuantity = Math.min(Math.abs(signedQuantity), tradeQuantity);
    if (signedQuantity > 0 && trade.side === 'SELL') {
      realizedPnl += (trade.price - averageEntry) * closeQuantity;
    } else if (signedQuantity < 0 && trade.side === 'BUY') {
      realizedPnl += (averageEntry - trade.price) * closeQuantity;
    }

    const previousQuantity = signedQuantity;
    signedQuantity += signedDelta;

    if (signedQuantity === 0) {
      averageEntry = 0;
    } else if (Math.sign(previousQuantity) !== Math.sign(signedQuantity)) {
      averageEntry = trade.price;
    }
  }

  return roundPnl(realizedPnl);
}

function buildSettlementAuditLegs<TTrade extends PositionTradeLike>(
  trades: readonly TTrade[],
  settlementFloorPrice: number | null,
  settlementExecutedAt: string | null,
) {
  let signedQuantity = 0;
  let totalCash = 0;
  let openSourceTradeIds = new Set<number>();
  const closedLegs: SettlementClosedLeg[] = [];

  for (const trade of sortTradesAscending(trades)) {
    const tradeQuantity = trade.quantity;
    if (tradeQuantity <= 0) continue;

    const signedDelta = trade.side === 'BUY' ? tradeQuantity : -tradeQuantity;
    const tradeCash = trade.side === 'BUY' ? -(trade.price * tradeQuantity) : trade.price * tradeQuantity;

    if (signedQuantity === 0 || Math.sign(signedQuantity) === Math.sign(signedDelta)) {
      signedQuantity += signedDelta;
      totalCash += tradeCash;
      openSourceTradeIds.add(trade.trade_id);
      continue;
    }

    const closedQuantity = Math.min(Math.abs(signedQuantity), Math.abs(signedDelta));
    const closedRatio = closedQuantity / Math.abs(signedQuantity);
    const closedBasisCash = totalCash * closedRatio;
    const perUnitTradeCash = tradeCash / Math.abs(signedDelta);
    const closingTradeCash = perUnitTradeCash * closedQuantity;

    closedLegs.push({
      entrySide: signedQuantity > 0 ? 'BUY' : 'SELL',
      exitSide: trade.side,
      entryPrice: roundPnl(Math.abs(closedBasisCash / closedQuantity)),
      exitPrice: roundPnl(Math.abs(closingTradeCash / closedQuantity)),
      quantity: roundPnl(closedQuantity),
      pnl: roundPnl(closedBasisCash + closingTradeCash),
      basisType: openSourceTradeIds.size > 1 ? 'AVG_BASIS' : 'MATCHED_FILL',
      executedAt: trade.executed_at ?? null,
      entryTradeCount: openSourceTradeIds.size,
      exitTradeCount: 1,
      sourceTradeCount: openSourceTradeIds.size + 1,
    });

    const remainingDelta = Math.abs(signedDelta) - closedQuantity;
    if (remainingDelta === 0) {
      signedQuantity += signedDelta;
      totalCash -= closedBasisCash;
      if (Math.abs(signedQuantity) < 0.0001) {
        openSourceTradeIds.clear();
      }
    } else {
      signedQuantity = signedDelta > 0 ? remainingDelta : -remainingDelta;
      totalCash = perUnitTradeCash * remainingDelta;
      openSourceTradeIds = new Set([trade.trade_id]);
    }

    if (Math.abs(signedQuantity) < 0.0001) {
      signedQuantity = 0;
      totalCash = 0;
      openSourceTradeIds.clear();
    }
  }

  const settledQuantity = Math.abs(signedQuantity);
  const settledLeg =
    settledQuantity > 0.0001
      ? {
          side: signedQuantity > 0 ? ('LONG' as const) : ('SHORT' as const),
          entryPrice: roundPnl(Math.abs(totalCash / settledQuantity)),
          floorPrice: settlementFloorPrice,
          quantity: roundPnl(settledQuantity),
          pnl:
            settlementFloorPrice == null
              ? null
              : roundPnl(totalCash + signedQuantity * settlementFloorPrice),
          basisType: 'NET_OPEN' as const,
          executedAt: settlementExecutedAt,
          sourceTradeCount: openSourceTradeIds.size,
        }
      : null;

  return {
    closedLegs,
    settledLeg,
  };
}

export function buildPositionTradeColumns<TTrade extends PositionTradeLike>({
  positionTeamCode,
  trades,
  totalTradingPnl = null,
  realizedPnl = null,
  unrealizedPnl = null,
}: BuildPositionTradeColumnsArgs<TTrade>) {
  const normalizedTeamCode = positionTeamCode.trim().toUpperCase();
  const positionTrades = trades.filter(
    (trade) =>
      trade.team_code.trim().toUpperCase() === normalizedTeamCode &&
      trade.trade_type !== 'SETTLEMENT',
  );

  const buyTrades = positionTrades.filter((trade) => trade.side === 'BUY');
  const sellTrades = positionTrades.filter((trade) => trade.side === 'SELL');
  const materializedPnl = calculateMaterializedTradePnl(positionTrades);
  const displayRealizedPnl = realizedPnl == null ? materializedPnl : realizedPnl;
  const displayUnrealizedPnl =
    unrealizedPnl == null
      ? totalTradingPnl == null
        ? null
        : roundPnl(totalTradingPnl - materializedPnl)
      : unrealizedPnl;

  return {
    buyTrades,
    sellTrades,
    buySummary: summarizeTrades(buyTrades),
    sellSummary: summarizeTrades(sellTrades),
    pnlBreakdown: {
      realizedPnl: roundPnl(displayRealizedPnl),
      unrealizedPnl: displayUnrealizedPnl == null ? null : roundPnl(displayUnrealizedPnl),
    },
  };
}

export function buildSettlementTradeAudit<
  TTrade extends PositionTradeLike,
  TSettlement extends SettlementTradeLike,
>({
  settlement,
  trades,
}: {
  settlement: TSettlement;
  trades: readonly TTrade[];
}): SettlementTradeAudit<TTrade> | null {
  if (settlement.trade_type !== 'SETTLEMENT') return null;

  const normalizedTeamCode = settlement.team_code.trim().toUpperCase();
  const sourceTradeIds = settlement.settlement_source_trade_ids?.length
    ? new Set(settlement.settlement_source_trade_ids)
    : null;
  const sourceTrades = sortTradesAscending(
    trades.filter((trade) => {
      if (trade.trade_type === 'SETTLEMENT') return false;
      if (trade.team_code.trim().toUpperCase() !== normalizedTeamCode) return false;
      return sourceTradeIds == null || sourceTradeIds.has(trade.trade_id);
    })
  );
  const settledQuantity = Math.abs(settlement.settlement_net_quantity ?? 0);
  const positionSide = settlement.settlement_position_side ?? 'LONG';
  const averageEntryPrice = settlement.settlement_entry_price ?? null;
  const settlementFloorPrice = settlement.settlement_floor_price ?? null;
  const totalPnl = settlement.settlement_total_pnl ?? settlement.settlement_pnl ?? null;
  const tradingPnl = settlement.settlement_trading_pnl ?? null;
  const openSettlementPnl = settlement.settlement_open_pnl ?? null;
  const closedQuantity = settlement.settlement_closed_quantity ?? 0;
  const { closedLegs, settledLeg } = buildSettlementAuditLegs(
    sourceTrades,
    settlementFloorPrice,
    settlement.executed_at ?? null,
  );

  return {
    positionSide,
    settledQuantity,
    averageEntryPrice,
    settlementFloorPrice,
    totalPnl,
    tradingPnl,
    openSettlementPnl,
    closedQuantity,
    closedEntryPrice: settlement.settlement_closed_entry_price ?? null,
    closedExitPrice: settlement.settlement_closed_exit_price ?? null,
    hasClosedBeforeSettlement: Math.abs(closedQuantity) > 0.0001,
    closedLegs,
    settledLeg,
    sourceTrades,
  };
}
